import { useState, useEffect, useRef, useMemo } from "react";
import { StatCard } from "@/components/downtime-stat-card";
import { CompressorTable } from "@/components/downtime-compressor-table";
import { CompressorDetailDialog } from "@/components/downtime-compressor-details";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CardHeaderActions } from "@/components/ui/card-header-actions";
import CustomDateRangePicker from "@/components/custom-date-range-picker";
import FilterDrawer from "@/components/filter-drawer";
import {
  AlertTriangle,
  Clock,
  Wrench,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import Sidebar from "@/components/sidebar";
import { Temporal } from "@js-temporal/polyfill";
import MobileMenuButton from "@/components/ui/mobile-menu-button";
import PageHeader from "@/components/page-header.jsx";
import PageHeaderActions from "@/components/page-header-actions.jsx";
import {
  getDateOffset,
  getTemporalDateOffset,
  formatDateLong,
} from "@/lib/datetime-utils.js";
import { getAllDowntimeEvents } from "@/api/downtime-api.js";
import {
  transformApiResponseToDowntimeEventSummaries,
  enrichDowntimeEventsWithAssetData,
  calculateDowntimeHours,
  calculateDowntimeHoursInRange,
  calculateAvailabilityPercentage,
  sameDowntimeAssetPk,
} from "@/lib/downtime-data-utils.js";
import {
  downloadDowntimeEventsPerAssets,
  downloadAssetPerformance,
} from "@/lib/csv-utils.js";

import { useFilters } from "@/hooks/useFilters";
import { useDowntime } from "@/contexts/DowntimeContext";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext";
import { useNoDataStatsRange } from "@/hooks/useNoDataStatsRange.js";
import { enrichRowsWithNoDataStats } from "@/lib/no-data-utils.js";
import { TIME_RANGE_OPTIONS } from "@/constants/chart-constants";
import { cn } from "@/lib/utils";
import { getStatusPriority } from "@/lib/status-utils.js";
import { cardStyles, dateRangePillIdle, dateRangePillSelected, dateRangePillTrack } from "@/lib/status-styles.js";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import { useColumnPreferences } from "@/hooks/useColumnPreferences.js";
import { ColumnVisibilityPopover } from "@/components/ui/column-visibility-popover.jsx";
import {
  DOWNTIME_COLUMNS,
  DOWNTIME_DEFAULT_VISIBLE_KEYS,
  DOWNTIME_ALL_KEYS,
} from "@/components/downtime-compressor-table";

export default function DowntimeEvents() {
  /** @type {[boolean, Function]} */
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use the custom filters hook
  const { filters, handleFilterChange, handleStatusClick, clearFilters } =
    useFilters();

  const { filteredAssets } = useFilteredAssets(filters, "");
  const allowedAssetIds = useMemo(
    () => new Set(filteredAssets.map((a) => Number(a.id))),
    [filteredAssets]
  );

  const [searchTerm, setSearchTerm] = useState(() => {
    const storedSearchTerm = localStorage.getItem("downtimeSearchTerm");
    return storedSearchTerm ? JSON.parse(storedSearchTerm) : "";
  });
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const storedSelectedLocation = filters.location;
    return Array.isArray(storedSelectedLocation) ? filters.location : [];
  });
  const [selectedVendor, setSelectedVendor] = useState(() => {
    const storedSelectedVendor = filters.vendor;
    return Array.isArray(storedSelectedVendor) ? filters.vendor : [];
  });
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const storedSelectedDateRange = localStorage.getItem(
      "downtimeSelectedDateRange"
    );
    return storedSelectedDateRange
      ? JSON.parse(storedSelectedDateRange)
      : "30d";
  });
  const [previousDateRange, setPreviousDateRange] = useState("30d");
  const [customStartDate, setCustomStartDate] = useState(() => {
    const storedCustomStartDate = localStorage.getItem(
      "downtimeCustomStartDate"
    );
    return storedCustomStartDate ? JSON.parse(storedCustomStartDate) : "";
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    const storedCustomEndDate = localStorage.getItem("downtimeCustomEndDate");
    return storedCustomEndDate ? JSON.parse(storedCustomEndDate) : "";
  });
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const [customDateRangeData, setCustomDateRangeData] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompressor, setSelectedCompressor] = useState(null);

  const {
    visibleKeys: downtimeVisibleKeys,
    toggleColumn: toggleDowntimeColumn,
    resetToDefaults: resetDowntimeColumns,
  } = useColumnPreferences(
    "downtimeTableColumns",
    DOWNTIME_DEFAULT_VISIBLE_KEYS,
    DOWNTIME_ALL_KEYS
  );

  const { downtimeEvents, isLoading, error } = useDowntime();
  const { assets } = useAssets();

  const { entry: noDataStatsEntry, statsByAssetId } = useNoDataStatsRange({
    selectedRange: selectedDateRange,
    customStart: customStartDate,
    customEnd: customEndDate,
    previousRange: previousDateRange,
  });

  // We need to distinguish between changes being made in this component, and changes made (while this component is mounted)
  // in an external component. These refs allow the useEffects down below to distinguish between those.
  const lastInternalLocationRef = useRef(null);
  const lastInternalVendorRef = useRef(null);
  const prevSelectedVendorRef = useRef(selectedVendor);

  useEffect(() => {
    localStorage.setItem("downtimeSearchTerm", JSON.stringify(searchTerm));
  }, [searchTerm]);

  // This useEffect will sync shared filters to local state when they change externally.
  // This ensures Downtime Events stays in sync with Fleet Overview and Fleet Map's vendor and location.
  // Only depends on filters, not local state, to avoid running when user changes selections, hence the
  // directive on the dependency array to not flag the non-exhaustive deps.
  useEffect(() => {
    const sharedLocation = Array.isArray(filters.location)
      ? filters.location
      : [];
    const sharedVendor = Array.isArray(filters.vendor) ? filters.vendor : [];

    // Check if this change came from the user setting it in this component
    const isInternalLocationUpdate =
      lastInternalLocationRef.current !== null
      && JSON.stringify(lastInternalLocationRef.current)
        === JSON.stringify(sharedLocation);
    const isInternalVendorUpdate =
      lastInternalVendorRef.current !== null
      && JSON.stringify(lastInternalVendorRef.current)
        === JSON.stringify(sharedVendor);

    // Skip if this is an internal update. This prevents circular re-renders. The component
    // already has the update so it does not need to respond to the sync update coming from
    // elsewhere.
    if (isInternalLocationUpdate || isInternalVendorUpdate) {
      // Clear the refs since we've processed this update already, otherwise additional changes made on the page won't work properly.
      if (isInternalLocationUpdate) {
        lastInternalLocationRef.current = null;
      }
      if (isInternalVendorUpdate) {
        lastInternalVendorRef.current = null;
      }
      return;
    }

    // If we're here, it was an external change in some other page and we're receiving the
    // sync. Local state now needs an update.
    const locationNeedsUpdate =
      JSON.stringify(sharedLocation) !== JSON.stringify(selectedLocation);
    const vendorNeedsUpdate =
      JSON.stringify(sharedVendor) !== JSON.stringify(selectedVendor);

    // If vendor is changing and location has values in shared filters, update both together.
    // This preserves the location during sync because the useEffect below that handles that
    //  will see that sharedLocation has values and won't clear it.
    if (vendorNeedsUpdate) {
      setSelectedVendor(sharedVendor);
      // If location also changed and has values, update it to preserve the selection
      if (locationNeedsUpdate && sharedLocation.length > 0) {
        setSelectedLocation(sharedLocation);
      }
    } else if (locationNeedsUpdate) {
      // Only location changed, update it normally
      setSelectedLocation(sharedLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.location, filters.vendor]);

  // This useEffect will sync local location state changes to filters elsewhere in the app.
  useEffect(() => {
    const currentFilterLocation = Array.isArray(filters.location)
      ? filters.location
      : [];

    // Only update if the value actually changed.
    if (
      JSON.stringify(selectedLocation) === JSON.stringify(currentFilterLocation)
    ) {
      return;
    }

    lastInternalLocationRef.current = selectedLocation;
    handleFilterChange("location", selectedLocation);
  }, [selectedLocation, handleFilterChange, filters.location]);

  // This useEffect clears location when vendor changes - but only if it is changes the user is making in this component.
  useEffect(() => {
    const vendorActuallyChanged =
      JSON.stringify(prevSelectedVendorRef.current)
      !== JSON.stringify(selectedVendor);

    // Ignore a re-render, that is already handled.
    if (!vendorActuallyChanged) {
      return;
    }

    const sharedVendor = Array.isArray(filters.vendor) ? filters.vendor : [];
    const sharedLocation = Array.isArray(filters.location)
      ? filters.location
      : [];

    // Check if this vendor change is a sync (selectedVendor matches shared filter)
    // or a user-initiated change (selectedVendor doesn't match yet).
    const isSyncing =
      JSON.stringify(sharedVendor) === JSON.stringify(selectedVendor);

    // Clear location.
    if (!isSyncing || sharedLocation.length === 0) {
      // Only clear if location isn't already empty, otherwise the state changes too many times for that condition.
      if (selectedLocation.length > 0) {
        setSelectedLocation([]);
      }
    }

    // Skip filter update if the value is still the same (otherwise unnecessary state changes & work for the app.)
    if (JSON.stringify(selectedVendor) === JSON.stringify(sharedVendor)) {
      prevSelectedVendorRef.current = selectedVendor;
      return;
    }

    // This is a change the user is making in this component, so we need to update the ref that tracks that.

    lastInternalVendorRef.current = selectedVendor;
    handleFilterChange("vendor", selectedVendor);

    // Update the previous vendor ref
    prevSelectedVendorRef.current = selectedVendor;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor, handleFilterChange, filters.vendor]);

  // Check for openAsset query parameter - if present the downtime details for that asset should be opened
  // Also, since the filters the user had applied previously needs to be preserved in this case, check the
  // parameters coming back so the filters can be restored.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const urlSearch = urlParams.get("search");
    const urlDateRange = urlParams.get("dateRange");
    const urlStartDate = urlParams.get("startDate");
    const urlEndDate = urlParams.get("endDate");
    const urlLocations = urlParams.get("locations");
    const urlVendors = urlParams.get("vendors");

    if (urlSearch) setSearchTerm(urlSearch);
    if (urlDateRange) setSelectedDateRange(urlDateRange);
    if (urlStartDate) setCustomStartDate(urlStartDate);
    if (urlEndDate) setCustomEndDate(urlEndDate);
    if (urlLocations) {
      try {
        setSelectedLocation(JSON.parse(urlLocations));
      } catch (e) {
        console.error("Failed to parse locations from URL:", e);
      }
    }
    if (urlVendors) {
      try {
        setSelectedVendor(JSON.parse(urlVendors));
      } catch (e) {
        console.error("Failed to parse vendors from URL:", e);
      }
    }
  }, []); // Only run once on mount

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const assetToOpen = urlParams.get("openAsset");

    if (
      assetToOpen
      && !dialogOpen
      && downtimeEvents
      && downtimeEvents.length > 0
    ) {
      const decoded = decodeURIComponent(assetToOpen);
      const compressor = downtimeEvents.find((c) =>
        sameDowntimeAssetPk(c.assetId, decoded)
      );

      if (compressor) {
        setSelectedCompressor(compressor);
        setDialogOpen(true);

        // Clear query parameters from URL without reloading
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, [downtimeEvents, dialogOpen]);

  /**
   * Create a Date object with a suitable time component from a local date string
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @param {boolean} isEndOfDay - If true, returns end of day (23:59:59.999); otherwise start of day (00:00:00.000)
   * @returns {Date} Date object with proper time component depending on it being a start or an end date.
   */
  const createDateFromCustomFilterInput = (dateString, isEndOfDay = false) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return isEndOfDay
      ? new Date(year, month - 1, day, 23, 59, 59, 999)
      : new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  /**
   * Create a Temporal.Instant object with a suitable time component from a local date string
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @param {boolean} isEndOfDay - If true, returns end of day (23:59:59.999); otherwise start of day (00:00:00.000)
   * @returns {Temporal.Instant} Temporal Instant based on start or an end of date.
   */
  const createTemporalInstantFromCustomFilterInput = (
    dateString,
    isEndOfDay = false
  ) => {
    const plainDate = Temporal.PlainDate.from(dateString);
    const zonedDateTime = isEndOfDay
      ? plainDate.toZonedDateTime({
          timeZone: Temporal.Now.timeZoneId(),
          plainTime: new Temporal.PlainTime(23, 59, 59, 999),
        })
      : plainDate.toZonedDateTime({ timeZone: Temporal.Now.timeZoneId() });
    return zonedDateTime.toInstant();
  };

  /**
   * Get date range for calculations (all dates in UTC)
   * @returns {Object} Object with startDate and endDate in UTC
   */
  const getDateRange = () => {
    if (selectedDateRange === "custom" && customStartDate && customEndDate) {
      return {
        startDate: createDateFromCustomFilterInput(customStartDate),
        endDate: createDateFromCustomFilterInput(customEndDate, true),
      };
    }

    const now = new Date();
    const range =
      selectedDateRange && selectedDateRange !== "custom"
        ? selectedDateRange
        : "30d";
    return {
      startDate: getDateOffset(now.toISOString(), range),
      endDate: now,
    };
  };

  /**
   * Get date range for calculations (all dates in Temporal.Instant)
   * @returns {Object} Object with startDate and endDate in Temporal.Instant
   */
  const getTemporalDateRange = () => {
    if (selectedDateRange === "custom" && customStartDate && customEndDate) {
      return {
        startDate: createTemporalInstantFromCustomFilterInput(customStartDate),
        endDate: createTemporalInstantFromCustomFilterInput(
          customEndDate,
          true
        ),
      };
    }

    const now = Temporal.Now.zonedDateTimeISO();
    const range =
      selectedDateRange && selectedDateRange !== "custom"
        ? selectedDateRange
        : "30d";
    return {
      startDate: getTemporalDateOffset(now, range),
      endDate: now,
    };
  };

  // Handle custom date changes from URL parameters or localStorage restoration on mount
  // Note: Direct apply button clicks are handled in the onApply handler
  const hasInitializedCustomDates = useRef(false);
  useEffect(() => {
    // Only auto-trigger once on initial mount if dates were restored from URL/localStorage
    if (
      !hasInitializedCustomDates.current
      && selectedDateRange === "custom"
      && customStartDate
      && customEndDate
    ) {
      hasInitializedCustomDates.current = true;
      handleCustomDateChange(customStartDate, customEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Re-enrich custom date range data when assets are updated
  useEffect(() => {
    // Use functional update to access current customDateRangeData without including it in deps.
    // This prevents infinite loops when customDateRangeData is updated.
    setCustomDateRangeData((currentData) => {
      if (currentData.length > 0 && assets.length > 0) {
        const needsEnrichment = currentData.some(
          (event) => !event.vendor || !event.status
        );

        if (needsEnrichment) {
          return enrichDowntimeEventsWithAssetData(currentData, assets);
        }
      }
      // Return current data unchanged if no enrichment needed to prevent unnecessary re-renders.
      return currentData;
    });
    // Specifically only depend on assets.length to avoid re-running on every assets array reference change.
    // And since we're using functional update for setCustomDateRangeData we don't need it in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.length]);

  /**
   * Check if custom date range is within the last 30 days
   * @param {string} startDate - Start date string (YYYY-MM-DD)
   * @param {string} endDate - End date string (YYYY-MM-DD)
   * @returns {boolean} True if within 30 days
   */
  const isCustomRangeWithin30Days = (startDate, endDate) => {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return start >= thirtyDaysAgo && end <= now;
  };

  /**
   * Filter downtime events by date range
   * @param {Array} events - Array of downtime events
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Array} Filtered events
   */
  const filterEventsByDateRange = (events, startDate, endDate) => {
    if (!Array.isArray(events) || !startDate || !endDate) return [];

    return events
      .map((compressor) => {
        const filteredEvents = compressor.events
          .filter((event) => {
            const eventStart = new Date(event.dateTime);
            // For ongoing events (active=true), treat as having no end date
            const eventEnd = event.active
              ? null
              : event.dateTimeEnd
                ? new Date(event.dateTimeEnd)
                : null;

            // Event overlaps with range if it starts before range ends AND ends after range starts (or is open)
            return (
              eventStart <= endDate
              && (eventEnd === null || eventEnd >= startDate)
            );
          })
          .map((event) => ({
            ...event,
            dtHours: calculateDowntimeHoursInRange(event, startDate, endDate),
          }));

        const downtimeHours = calculateDowntimeHours(filteredEvents);

        return {
          ...compressor,
          events: filteredEvents,
          ...downtimeHours,
          eventCount: filteredEvents.length,
        };
      })
      .filter((compressor) => compressor.eventCount > 0);
  };

  /**
   * Handle custom date range changes
   * @param {string} startDate - Start date string (YYYY-MM-DD)
   * @param {string} endDate - End date string (YYYY-MM-DD)
   */
  const handleCustomDateChange = async (startDate, endDate) => {
    if (!startDate || !endDate) {
      return;
    }

    // Check if range is within 30 days
    if (isCustomRangeWithin30Days(startDate, endDate)) {
      setCustomDateRangeData([]); // Filtering existing data - no API call needed
      return;
    }

    // Range goes beyond 30 days, need to fetch new data
    setIsCustomLoading(true);
    try {
      const startDateTime = createDateFromCustomFilterInput(startDate);
      const endDateTime = createDateFromCustomFilterInput(endDate, true);

      const apiResponse = await getAllDowntimeEvents(
        startDateTime.toISOString(),
        endDateTime.toISOString()
      );

      const transformedDowntimes = transformApiResponseToDowntimeEventSummaries(
        apiResponse,
        startDateTime,
        endDateTime
      );

      const enrichedDowntimes = enrichDowntimeEventsWithAssetData(
        Array.isArray(transformedDowntimes) ? transformedDowntimes : [],
        assets
      );

      setCustomDateRangeData(enrichedDowntimes);
    } catch (err) {
      console.error("Error fetching custom date range data:", err);
      setCustomDateRangeData([]);
    } finally {
      setIsCustomLoading(false);
    }
  };

  const handleCompressorClick = (clickedAssetId) => {
    const compressor = filteredAndSortedCompressors.find((c) =>
      sameDowntimeAssetPk(c.assetId, clickedAssetId)
    );

    if (compressor) {
      setSelectedCompressor(compressor);
      setDialogOpen(true);
    }
  };

  const clearDateTimeFilters = () => {
    setSearchTerm("");
    setSelectedLocation([]);
    setSelectedVendor([]);
    setSelectedDateRange("30d");
    setPreviousDateRange("30d");
    setCustomStartDate("");
    setCustomEndDate("");
    setCustomDateRangeData([]);
    clearFilters();
  };

  /**
   * Handles column header click for sorting
   * @param {string} column - Column name to sort by
   */
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleToggleDowntimeColumn = (key) => {
    const col = DOWNTIME_COLUMNS.find((c) => c.key === key);
    if (col?.sortKey && col.sortKey === sortColumn && downtimeVisibleKeys.includes(key)) {
      setSortColumn(null);
      setSortDirection("asc");
    }
    toggleDowntimeColumn(key);
  };

  /**
   * Gets the sort icon for a column
   * @param {string} column - Column name
   * @returns {JSX.Element} Sort icon component
   */
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? (
        <ChevronUp className='h-4 w-4 ml-1 text-muted-foreground' />
      ) : (
        <ChevronDown className='h-4 w-4 ml-1 text-muted-foreground' />
      );
    }
    return (
      <ChevronsUpDown className='h-4 w-4 ml-1 text-muted-foreground opacity-50' />
    );
  };

  /**
   * Sort downtime events by column
   * @param {Array} events - Array of downtime events to sort
   * @param {string} column - Column to sort by
   * @param {string} direction - Sort direction ('asc' or 'desc')
   * @returns {Array} Sorted events array
   */
  const sortDowntimeEventsByColumn = (events, column, direction) => {
    if (!Array.isArray(events)) return [];

    const { startDate, endDate } = getDateRange();
    const availabilityFor = (compressor, downtimeField) =>
      calculateAvailabilityPercentage(
        compressor[downtimeField] || 0,
        startDate,
        endDate
      );

    const numericColumns = ["totalDTHours", "mechanicalDTHours", "eventCount"];
    const numericAssetColumns = ["horsepower", "latitude", "longitude"];
    const dateAssetColumns = ["lastFaultTime", "lastUpdated"];
    const assetColumns = [
      "telemetrySource", "optimizationEnabled", "lastFault", "lastFaultTime",
      "lastUpdated", "foreman", "customer", "horsepower", "compressorModel",
      "engineModel", "latitude", "longitude",
    ];

    return [...events].sort((a, b) => {
      let aValue, bValue;

      // Calculate values based on column type
      if (column === "runtime") {
        aValue = availabilityFor(a, "totalDTHours");
        bValue = availabilityFor(b, "totalDTHours");
      } else if (column === "mechanicalAvailability") {
        aValue = availabilityFor(a, "mechanicalDTHours");
        bValue = availabilityFor(b, "mechanicalDTHours");
      } else if (column === "communicationsAvailability") {
        aValue = Number.isFinite(a.communicationsAvailabilityPercent)
          ? a.communicationsAvailabilityPercent
          : -1;
        bValue = Number.isFinite(b.communicationsAvailabilityPercent)
          ? b.communicationsAvailabilityPercent
          : -1;
      } else if (column === "lastDowntimeTime") {
        // Get the latest event's dateTime for sorting
        const getLatestEventTime = (compressor) => {
          if (!compressor.events || compressor.events.length === 0) return 0;
          const sorted = [...compressor.events].sort(
            (x, y) =>
              new Date(y.dateTime).getTime() - new Date(x.dateTime).getTime()
          );
          return new Date(sorted[0].dateTime).getTime();
        };
        aValue = getLatestEventTime(a);
        bValue = getLatestEventTime(b);
      } else if (assetColumns.includes(column)) {
        const assetA = assets?.find((asset) => String(asset.id) === String(a.assetId));
        const assetB = assets?.find((asset) => String(asset.id) === String(b.assetId));
        if (dateAssetColumns.includes(column)) {
          aValue = assetA?.[column] ? new Date(assetA[column]).getTime() : 0;
          bValue = assetB?.[column] ? new Date(assetB[column]).getTime() : 0;
        } else if (numericAssetColumns.includes(column)) {
          aValue = assetA?.[column] != null ? Number(assetA[column]) : -Infinity;
          bValue = assetB?.[column] != null ? Number(assetB[column]) : -Infinity;
        } else if (column === "optimizationEnabled") {
          aValue = assetA?.optimizationEnabled ? 1 : 0;
          bValue = assetB?.optimizationEnabled ? 1 : 0;
        } else {
          const comparison = String(assetA?.[column] || "").localeCompare(
            String(assetB?.[column] || "")
          );
          return direction === "asc" ? comparison : -comparison;
        }
      } else if (numericColumns.includes(column)) {
        aValue = Number(a[column] || 0);
        bValue = Number(b[column] || 0);
      } else {
        // String sorting
        const comparison = String(a[column] || "").localeCompare(
          String(b[column] || "")
        );
        return direction === "asc" ? comparison : -comparison;
      }

      // Numeric comparison
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // First filter by date range, then apply other filters
  let dateFilteredEvents;
  let effectiveDateRange;

  if (selectedDateRange === "custom" && customStartDate && customEndDate) {
    effectiveDateRange = "custom";
    const startDate = createDateFromCustomFilterInput(customStartDate);
    const endDate = createDateFromCustomFilterInput(customEndDate, true);

    const withinRange = isCustomRangeWithin30Days(
      customStartDate,
      customEndDate
    );
    const eventsToFilter = withinRange
      ? downtimeEvents || []
      : customDateRangeData;

    dateFilteredEvents = filterEventsByDateRange(
      eventsToFilter,
      startDate,
      endDate
    );
  } else {
    // Use standard date range filtering
    effectiveDateRange =
      selectedDateRange === "custom" ? previousDateRange : selectedDateRange;
    const now = new Date();
    const startDate = getDateOffset(now.toISOString(), effectiveDateRange);
    dateFilteredEvents = filterEventsByDateRange(
      downtimeEvents || [],
      startDate,
      now
    );
  }

  // Import status priority utility
  const getStatusPriorityForSort = (status) => {
    // For sorting, we want lower numbers first (most critical first)
    // So we invert the priority: highest priority (most critical) becomes lowest sort number
    const priority = getStatusPriority(status);
    // Invert: 6 (offline) -> 1, 5 (danger) -> 2, etc.
    // Unknown/available (0) should sort last, so give them high number
    return priority > 0 ? 7 - priority : 8;
  };

  // Filter compressors based on search term and other filters
  const filteredCompressors = dateFilteredEvents.filter((compressor) => {
    if (!compressor || typeof compressor !== "object") return false;

    if (!allowedAssetIds.has(Number(compressor.assetId))) {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const searchableFields = [
        compressor.assetNumber,
        compressor.assetLocation,
        compressor.vendor,
        compressor.status,
        compressor.assetLease,
        compressor.totalDTHours,
        compressor.mechanicalDTHours,
        compressor.nonMechanicalDTHours,
        compressor.eventCount,
      ];

      const matchesFields = searchableFields.some((field) =>
        field?.toString().toLowerCase().includes(searchLower)
      );

      const matchesEvents = compressor.events?.some(
        (event) =>
          event.relatedAlarm?.toLowerCase().includes(searchLower)
          || (event.dateTime
            && formatDateLong(event.dateTime)
              .toLowerCase()
              .includes(searchLower))
      );

      if (!matchesFields && !matchesEvents) return false;
    }

    return true;
  });

  const enrichedFilteredCompressors = useMemo(
    () =>
      enrichRowsWithNoDataStats(filteredCompressors, statsByAssetId, {
        idField: "assetId",
        availabilityField: "communicationsAvailabilityPercent",
      }),
    [filteredCompressors, statsByAssetId]
  );

  // Apply sorting to filtered compressors
  const filteredAndSortedCompressors = sortColumn
    ? sortDowntimeEventsByColumn(
        enrichedFilteredCompressors,
        sortColumn,
        sortDirection
      )
    : enrichedFilteredCompressors.sort((a, b) => {
        // Default sort by status, then by asset name
        const statusPriorityA = getStatusPriorityForSort(a.status);
        const statusPriorityB = getStatusPriorityForSort(b.status);

        if (statusPriorityA !== statusPriorityB) {
          return statusPriorityA - statusPriorityB;
        }

        // Secondary sort by asset name (ascending)
        return (a.assetNumber || "").localeCompare(b.assetNumber || "");
      });

  const totalDowntime = filteredCompressors.reduce(
    (sum, c) => sum + (c.totalDTHours || 0),
    0
  );
  const totalMechanicalDT = filteredCompressors.reduce(
    (sum, c) => sum + (c.mechanicalDTHours || 0),
    0
  );
  const totalEvents = filteredCompressors.reduce(
    (sum, c) => sum + (c.eventCount || 0),
    0
  );

  /**
   * Calculate average percentage across all compressors
   * @param {Function} calculationFunction - Function to calculate percentage for each compressor
   * @returns {number} Average percentage
   */
  const calculateAveragePercentage = (calculationFunction) => {
    if (filteredCompressors.length === 0) return 0;

    const percentages = filteredCompressors.map(calculationFunction);
    return (
      percentages.reduce((sum, percentage) => sum + percentage, 0)
      / percentages.length
    );
  };

  const { startDate: summaryStartDate, endDate: summaryEndDate } =
    getDateRange();
  const averageRuntime = calculateAveragePercentage((c) =>
    calculateAvailabilityPercentage(
      c.totalDTHours || 0,
      summaryStartDate,
      summaryEndDate
    )
  );
  const averageMechanicalAvailability = calculateAveragePercentage((c) =>
    calculateAvailabilityPercentage(
      c.mechanicalDTHours || 0,
      summaryStartDate,
      summaryEndDate
    )
  );

  // Show error state
  if (error) {
    return (
      <div className='flex h-screen bg-background relative'>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <div className='text-destructive mb-4'>
              <AlertTriangle className='h-12 w-12 mx-auto' />
            </div>
            <p className='text-destructive mb-2'>Error loading downtime events</p>
            <p className='text-muted-foreground text-sm'>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex h-screen bg-background relative'>
      {/* Mobile menu button */}
      <MobileMenuButton
        isOpen={isMobileMenuOpen}
        onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Sidebar */}
      <Sidebar
        key='persistent-sidebar'
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      <div className='flex-1 flex flex-col min-h-0 overflow-hidden'>
        <PageHeader
          icon={AlertTriangle}
          title='Downtime Events'
          subtitle='Compressor Maintenance & Prioritization'>
          <PageHeaderActions
            showLastUpdated
            lastUpdatedContext='downtime'
          />
        </PageHeader>

        <div className={cardStyles.pageContent}>
          <div className='flex flex-col flex-1 min-h-full gap-6'>
            <div className='shrink-0'>
              <FilterDrawer
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearDateTimeFilters}
                onStatusClick={handleStatusClick}
                searchTerm={searchTerm}
              />
            </div>

            {/* Date range + KPI cards — tight spacing (mock) */}
            <div className='shrink-0 space-y-3'>
              <div
                className={cn(
                  "flex min-w-0 flex-wrap items-center gap-y-2",
                  selectedDateRange === "custom"
                    ? "w-full"
                    : "justify-start gap-x-2"
                )}>
                {selectedDateRange === "custom" ? (
                  <CustomDateRangePicker
                    startDate={customStartDate}
                    endDate={customEndDate}
                    onApply={(start, end) => {
                      setCustomStartDate(start);
                      setCustomEndDate(end);
                      handleCustomDateChange(start, end);
                      localStorage.setItem(
                        "downtimeCustomStartDate",
                        JSON.stringify(start)
                      );
                      localStorage.setItem(
                        "downtimeCustomEndDate",
                        JSON.stringify(end)
                      );
                      localStorage.setItem(
                        "downtimeSelectedDateRange",
                        JSON.stringify("custom")
                      );
                    }}
                    onClear={() => {
                      setSelectedDateRange(previousDateRange);
                      setCustomStartDate("");
                      setCustomEndDate("");
                      setCustomDateRangeData([]);
                      localStorage.setItem(
                        "downtimeCustomStartDate",
                        JSON.stringify("")
                      );
                      localStorage.setItem(
                        "downtimeCustomEndDate",
                        JSON.stringify("")
                      );
                      localStorage.setItem(
                        "downtimeSelectedDateRange",
                        JSON.stringify("30d")
                      );
                    }}
                    disabled={isCustomLoading}
                  />
                ) : (
                  <div className={cn(dateRangePillTrack, "justify-start")}>
                    {TIME_RANGE_OPTIONS.map((rangeLabel, index) => {
                      const internalRange = ["24h", "7d", "30d", "custom"][
                        index
                      ];
                      return (
                        <Button
                          key={internalRange}
                          variant='outline'
                          size='sm'
                          disabled={isCustomLoading}
                          onClick={() => {
                            if (internalRange === "custom") {
                              setPreviousDateRange(selectedDateRange);
                            }
                            setSelectedDateRange(internalRange);
                            localStorage.setItem(
                              "downtimeSelectedDateRange",
                              JSON.stringify(internalRange)
                            );
                          }}
                          className={cn(
                            "rounded-full whitespace-nowrap shadow-none font-medium",
                            selectedDateRange === internalRange
                              ? dateRangePillSelected
                              : dateRangePillIdle
                          )}>
                          {rangeLabel}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {isLoading || isCustomLoading ? (
                  [...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className='bg-card rounded-lg border border-border shadow-sm p-4'>
                      <LoadingSkeleton className='h-5 w-28 rounded mb-3' />
                      <LoadingSkeleton className='h-8 w-20 rounded mb-2' />
                      <LoadingSkeleton className='h-4 w-32 rounded' />
                    </div>
                  ))
                ) : (
                  <>
                    <StatCard
                      title='Total Downtime'
                      value={`${totalDowntime.toFixed(1)} h`}
                      subtitle={`${
                        effectiveDateRange === "24h"
                          ? "Last 24 hours"
                          : effectiveDateRange === "7d"
                            ? "Last 7 days"
                            : selectedDateRange === "custom"
                              ? "Custom range"
                              : "Last 30 days"
                      }`}
                      secondaryTitle='Average Runtime'
                      secondaryValue={`${averageRuntime.toFixed(1)}%`}
                      icon={Clock}
                      status='summary'
                    />
                    <StatCard
                      title='Mechanical Downtime'
                      value={`${totalMechanicalDT.toFixed(1)} h`}
                      subtitle={
                        totalDowntime > 0
                          ? `${((totalMechanicalDT / totalDowntime) * 100).toFixed(0)}% of total`
                          : "0% of total"
                      }
                      secondaryTitle='Average Mechanical Availability'
                      secondaryValue={`${averageMechanicalAvailability.toFixed(1)}%`}
                      icon={Wrench}
                      status='mechanical'
                    />
                    <StatCard
                      title='Total Events'
                      value={totalEvents}
                      subtitle='Downtime occurrences'
                      icon={AlertTriangle}
                      status='events'
                    />
                  </>
                )}
              </div>
            </div>

            {/* Table — fills remaining height with a scrollable floor */}
            <div className={cardStyles.tableFillRegion}>
              {isLoading || isCustomLoading ? (
                <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden flex flex-col flex-1 min-h-0 h-full'>
                  <CardHeader className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 border-b border-border'>
                    <LoadingSkeleton className='h-6 w-40 rounded' />
                    <div className='flex items-center gap-4'>
                      <LoadingSkeleton className='h-9 w-48 rounded' />
                      <LoadingSkeleton className='h-9 w-8 rounded' />
                      <LoadingSkeleton className='h-9 w-8 rounded' />
                    </div>
                  </CardHeader>
                  <CardContent className='p-0 flex flex-col flex-1 min-h-0'>
                    <div
                      className={cn(
                        cardStyles.tableContainer,
                        cardStyles.tableFillBody
                      )}>
                      <Table className='w-full'>
                        <TableHeader>
                          <TableRow className='border-b border-border'>
                            {[...Array(6)].map((_, i) => (
                              <TableHead
                                key={i}
                                className={cn(
                                  cardStyles.tableHead,
                                  "cursor-default"
                                )}>
                                <LoadingSkeleton className='h-5 w-20 rounded' />
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[...Array(6)].map((_, rowIndex) => (
                            <TableRow
                              key={rowIndex}
                              className={cardStyles.bodyRow}>
                              {[...Array(6)].map((_, colIndex) => (
                                <TableCell
                                  key={colIndex}
                                  className={cardStyles.tableCell}>
                                  <LoadingSkeleton className='h-4 w-16 rounded' />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden flex flex-col flex-1 min-h-0 h-full'>
                  <CardHeaderActions
                    title='Downtime Events'
                    actions={[
                      {
                        id: 1,
                        text: "Asset Performance (.csv)",
                        executeAction: () => {
                          downloadAssetPerformance(
                            getTemporalDateRange(),
                            enrichedFilteredCompressors
                          );
                        },
                        actionType: "Download",
                      },
                      {
                        id: 2,
                        text: "Raw Events (.csv)",
                        executeAction: () => {
                          downloadDowntimeEventsPerAssets(
                            getTemporalDateRange(),
                            filteredCompressors
                          );
                        },
                        actionType: "Download",
                      },
                      {
                        id: 3,
                        element: (
                          <ColumnVisibilityPopover
                            columns={DOWNTIME_COLUMNS}
                            visibleKeys={downtimeVisibleKeys}
                            onToggle={handleToggleDowntimeColumn}
                            onReset={resetDowntimeColumns}
                          />
                        ),
                      },
                    ]}
                    searchEnabled={true}
                    searchValue={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder='Search compressors...'
                    searchTestId='input-search-compressors'
                    titleTestId='title-downtime-events'
                  />
                  <CardContent
                    className={cn(
                      cardStyles.content,
                      "flex flex-col flex-1 min-h-0"
                    )}>
                    <CompressorTable
                      compressors={filteredAndSortedCompressors}
                      onRowClick={handleCompressorClick}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      getSortIcon={getSortIcon}
                      selectedDateRange={selectedDateRange}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                      visibleKeys={downtimeVisibleKeys}
                      noDataStatsLoading={noDataStatsEntry.isLoading}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            <div className={cardStyles.pageBottomSpacer} aria-hidden='true' />

            <CompressorDetailDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              compressor={selectedCompressor}
              selectedDateRange={selectedDateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              searchTerm={searchTerm}
              selectedLocation={selectedLocation}
              selectedVendor={selectedVendor}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
